/* istanbul ignore file */

import { useState } from 'react';
import { useSelector } from 'react-redux';

import { AccordionDetails, AccordionGroup, AccordionSummary } from '@interstate/components/AccordionGroup';
import { Action } from '@interstate/components/Action';
import { Badge } from '@interstate/components/Badge';
import { CheckBox } from '@interstate/components/CheckBox';
import { Grid } from '@interstate/components/Grid';
import { Accordion } from '@interstate/components/AccordionGroup';
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';
import { kebabCase } from '@makemydeal/dr-common-utils';

import type {
    AppliedFilter,
    CheckboxFilterGroup,
    ExpandedFiltersAccordionState,
    ExpandedFiltersState,
    FilterGroup,
    FilterItem,
    FilterItemValue
} from '../../types';
import { isCheckboxFilter, isInputFilter } from '../../types';
import { MAX_VISIBLE_FILTER_ITEMS, MAX_SELECTED_FILTER_ITEMS } from '../../constants';
import { countAppliedItems, getFilterOptionLabel, normalizeValue } from '../../utils';
import { useVehicleSearchFiltersContext } from './VehicleSearchFiltersContext';
import VehicleSearchRangeFilterInput from './VehicleSearchRangeFilterInput';
import { changeVehicleSelectors } from '../../store';
import { FilterOptionsWrapper } from './VehicleSearchFilters.style';

const VehicleSearchFilters = () => {
    const { filterList, selectedFilters, updateFilterValue, isSearchError } = useVehicleSearchFiltersContext();
    const isFiltersInitialized = useSelector(changeVehicleSelectors.getIsFiltersInitialized);
    const dealerships = useSelector(changeVehicleSelectors.getDealerships);
    const currentDealer = useSelector(changeVehicleSelectors.getCurrentDealer);

    const [expandedFilters, setExpandedFilters] = useState<ExpandedFiltersState>({});
    const [isAccordionCollapsed, setIsAccordionCollapsed] = useState<ExpandedFiltersAccordionState>({});
    const isMaxFiltersReached = selectedFilters.length >= MAX_SELECTED_FILTER_ITEMS;

    const expandFilter = (filterId: string) => {
        setExpandedFilters((prev: ExpandedFiltersState) => ({
            ...prev,
            [filterId]: true
        }));
    };

    const expandFilterAccordion = (accordionId: string, set: boolean) => {
        setIsAccordionCollapsed((prev: ExpandedFiltersAccordionState) => ({
            ...prev,
            [accordionId]: set
        }));
    };

    const getVisibleItems = (filter: CheckboxFilterGroup) => {
        const isExpanded = expandedFilters[filter.id] || false;

        return isExpanded ? filter.items : filter.items.slice(0, MAX_VISIBLE_FILTER_ITEMS);
    };

    const handleApplyFilter = (filter: FilterGroup, item: FilterItem, value: FilterItemValue) => {
        const { id, type, title } = filter;

        const appliedFilter = {
            ...item,
            id,
            type,
            title,
            // Store original label for display but ensure comparison is case-insensitive
            normalizedLabel: typeof item.label === 'string' ? normalizeValue(item.label) : item.label
        } as AppliedFilter;
        updateFilterValue(appliedFilter, value);
    };

    return (
        <div data-testid="vehicle-search-filters">
            <Grid container>
                {isSearchError && isFiltersInitialized && (
                    <Grid xs={12}>
                        <Alert type="error" sx={{ marginBottom: '16px' }} data-testid="unable-to-load-filters-alert">
                            <Typography variant="body-sm" tag="span">
                                Unable to load additional filters due to an error with a selected option.
                            </Typography>
                        </Alert>
                    </Grid>
                )}
                {isMaxFiltersReached && (
                    <Grid xs={12}>
                        <Alert type="caution" sx={{ marginBottom: '16px' }} data-testid="max-filters-reached-warning">
                            <Typography variant="body-sm" tag="span">
                                You’ve reached the maximum of 20 filters. To add more, please remove one or more from your current
                                selection.
                            </Typography>
                        </Alert>
                    </Grid>
                )}
            </Grid>
            <AccordionGroup accordionType="multi-expanded">
                {filterList.map((filterGroup) => {
                    const isExpanded = expandedFilters[filterGroup.id] || false;
                    const isCheckbox = isCheckboxFilter(filterGroup);
                    const showMoreToggle = isCheckbox && filterGroup.items.length > MAX_VISIBLE_FILTER_ITEMS;
                    const isAccordionExpanded = isAccordionCollapsed[filterGroup.id] || false;

                    return (
                        <Accordion
                            key={filterGroup.title}
                            onChange={() => expandFilterAccordion(filterGroup.id, !isAccordionExpanded)}
                            sx={{ marginBottom: 0 }}
                            expanded={isAccordionExpanded}
                        >
                            <AccordionSummary
                                badge={
                                    countAppliedItems(filterGroup) > 0 &&
                                    !isAccordionExpanded && (
                                        <Badge variant="light" data-testid="applied-filters-count">
                                            {countAppliedItems(filterGroup)}
                                        </Badge>
                                    )
                                }
                                chevronPosition="left"
                                data-testid={`filter-accordion-title-${kebabCase(filterGroup.title)}`}
                            >
                                <span>{filterGroup.title}</span>
                            </AccordionSummary>
                            <AccordionDetails>
                                {isCheckboxFilter(filterGroup) && (
                                    <FilterOptionsWrapper>
                                        {getVisibleItems(filterGroup).map((item) => (
                                            <CheckBox
                                                key={item.label}
                                                label={getFilterOptionLabel(item.label, filterGroup.id, dealerships, currentDealer)}
                                                value={`${filterGroup.id}/${item.label}`}
                                                disabled={isMaxFiltersReached && !item.value}
                                                checked={Boolean(item.value)}
                                                data-testid={`filter-checkbox-${kebabCase(item.label)}`}
                                                sx={{ backgroundColor: 'inherit' }}
                                                onChange={(event) => {
                                                    const { checked } = event.target.value;
                                                    handleApplyFilter(filterGroup, item, checked);
                                                }}
                                            />
                                        ))}
                                    </FilterOptionsWrapper>
                                )}

                                {isInputFilter(filterGroup) && (
                                    <Grid container spacing={2}>
                                        <VehicleSearchRangeFilterInput
                                            filterGroup={filterGroup}
                                            isMaxFiltersReached={isMaxFiltersReached}
                                            onChange={(item, value) => {
                                                handleApplyFilter(filterGroup, item, value);
                                            }}
                                        />
                                    </Grid>
                                )}
                                {showMoreToggle && !isExpanded && (
                                    <Grid xs={12}>
                                        <Action
                                            size="sm"
                                            onClick={() => expandFilter(filterGroup.id)}
                                            data-testid="show-more-button"
                                        >
                                            Show more
                                        </Action>
                                    </Grid>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </AccordionGroup>
        </div>
    );
};

export default VehicleSearchFilters;
